import { generateBreadcrumbSchema } from '~/plugins/structuredData/breadcrumb'
import { generateItemListShema } from '~/plugins/structuredData/ItemList'
import { generateEstimatedSalarySchema } from '~/plugins/structuredData/estimatedSalary'
import { generateFAQPageSchema } from '~/plugins/structuredData/FAQPage'
import { generateEmployerAggregateRatingSchema } from '~/plugins/structuredData/employerAggregateRating'
import { generateReviewsListSchema } from '~/plugins/structuredData/review'
import { companyLinks, type MenuLink } from '~/utils/menuLinks'
import { applyMetaData } from '~/utils/meta/metaUtils'
import { getStateFullName } from '~/utils/states'
import { generateSalaryDescription } from '~/utils/salaryDescription'
import { generateSalaryMethodologyText } from '~/utils/salaryMethodology'
import {
  generateSalaryFAQQuestions,
  convertFAQEntriesToSchemaItems,
  type SalaryFAQData
} from '~/utils/salaryFAQ'
interface CompanyMetaOptions {
  companyName?: string
  companySlug?: string
  section?: string
  formattedLocation?: string
  formattedRole?: string
  location?: {
    city: string | null
    state: string | null
  }
  roleParam?: string | null
  keywordParam?: string | null
  companyLogo?: string
  noIndex?: boolean
  invalidLocation?: boolean
  industry?: string
  invalidRole?: boolean
  roleInfo?: { description?: string, soc_code?: string, title?: string } | null
  salaryDetail?: {
    success: boolean
    data?: {
      job_title: string
      location: string
      base_salary: {
        P10: number
        P25: number
        P50: number
        P75: number
        P90: number
      }
      count: number
      filters: {
        role: string
        state: string | null
        city: string | null
      }
    }
  }
  salaryPageTitle?: string
  dressCodeData?: any
  drugTestData?: any
  hiringProcessData?: any
  companies?: Array<{
    permalink: string
    company_name?: string
    brand_name?: string
    description?: string
  }>
  reviewStats?: {
    total: number
    overallRating: number
  }
  page?: number
  reviews?: Array<{
    _id: string
    title?: string
    text: string
    overall_rating: number
    date: Date | string
    status: string
    pros?: string
    cons?: string
    job_title?: string
    author?: {
      name?: string
      role?: string
    }
  }>
  companyDescription?: string
  companyWebsite?: string
}
function formatLocationDisplay(
  location: { city: string | null, state: string | null } | undefined
): string {
  if (!location) return ''
  if (location.city && location.state) {
    return `${location.city}, ${getStateFullName(location.state)}`
  } else if (location.state) {
    return getStateFullName(location.state)
  } else if (location.city) {
    return location.city
  }
  return ''
}
function getCurrentDate(): { year: string, month: string, monthsYear: string } {
  const now = new Date()
  const year = now.getFullYear().toString()
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ]
  const month = monthNames[now.getMonth()]
  return {
    year,
    month,
    monthsYear: `${month} ${year}`
  }
}
export function generateCompanyMeta({
  companyName,
  companySlug,
  section,
  location = { city: null, state: null },
  roleParam = null,
  keywordParam = null,
  companyLogo = '',
  noIndex = false,
  invalidLocation = false,
  invalidRole = false,
  industry = '',
  roleInfo = null,
  salaryDetail = null,
  salaryPageTitle = null,
  dressCodeData = null,
  drugTestData = null,
  hiringProcessData = null,
  companies = [],
  reviewStats = null,
  page = 1,
  reviews = [],
  companyDescription = '',
  companyWebsite = ''
}: CompanyMetaOptions = {}) {
  if (!companyName && !companySlug) {
    const title = `Find Company Ratings and Reviews from Employees on employers.io`
    const description = `Explore company reviews from real employees. Search for insights about workplace experiences, salaries, and career opportunities on employers.io.`
    const canonicalUrl = `/company`
    const schemas = []
    if (companies && companies.length > 0) {
      const companyLinks = companies.map(
        company =>
        ({
          id: company.permalink,
          label: company.company_name || company.brand_name,
          to: `/company/${company.permalink}`,
          name: company.company_name || company.brand_name,
          description: company.description || '',
          target: '_self',
          icon: ''
        } as MenuLink)
      )
      const companiesListSchema = generateItemListShema(companyLinks)
      if (companiesListSchema) {
        schemas.push(JSON.stringify(companiesListSchema))
      }
    }
    applyMetaData({
      title,
      description,
      canonical: canonicalUrl,
      index: true,
      follow: true,
      schemas: schemas.length > 0 ? schemas : undefined
    })
    return {
      title,
      description,
      canonicalUrl
    }
  }
  const urlFormattedRole = roleParam ? formatForUrl(roleParam) : ''
  const urlFormattedLocation = (() => {
    if (location.city && location.state) {
      return `${formatForUrl(location.city)}-${location.state.toUpperCase()}`
    } else if (location.state) {
      const stateFullName = getStateFullName(location.state)
      return formatForUrl(stateFullName)
    }
    return ''
  })()
  const roleName = roleParam ? roleParam.replace(/-/g, ' ') : ''
  const formattedLocation = formatLocationDisplay(location)
  const { year, month, monthsYear } = getCurrentDate()
  const baseUrl = `https://employers.io/company/${companySlug}`
  let title = ''
  let description = ''
  let canonicalUrl = baseUrl
  switch (section) {
    case 'overview':
      title = `Working at ${companyName}: Reviews, Salaries, and Jobs in ${year}`
      description = `Explore reviews, salaries, and job opportunities at ${companyName} in ${year}. Learn what it's like to work at ${companyName} on employers.io.`
      canonicalUrl = baseUrl
      break
    case 'dress-code':
      title = `${companyName} Employee Dress Code Policy ${year}: What to Wear for Different Jobs`
      description = `Learn about ${companyName}'s dress code policy for various job roles. Find out what to wear, what's not allowed, and get interview attire tips.`
      canonicalUrl = `${baseUrl}/dress-code`
      break
    case 'drug-test-policy':
      title = `Does ${companyName} Drug Test? - ${companyName} Drug Test Policy`
      description = `Does ${companyName} drug test before hiring? Learn about ${companyName}'s pre-employment and random testing policies, what substances are screened.`
      canonicalUrl = `${baseUrl}/drug-test-policy`
      break
    case 'hiring-process':
      title = `${companyName} Hiring Process ${year}: Application Steps, Interview Questions & Timeline`
      description = `Learn about the ${companyName} hiring process. Discover application steps, interview questions, timeline, and tips to help you land your job at ${companyName}.`
      canonicalUrl = `${baseUrl}/hiring-process`
      break
    case 'reviews':
      if (roleName && formattedLocation) {
        title = `${companyName} ${roleName} Job Reviews in ${formattedLocation}`
        description = `Find out what it's like to work as a ${roleName} at ${companyName} in ${formattedLocation}. Honest feedback on workplace culture, pay, management, and more.`
        canonicalUrl = `${baseUrl}/reviews/${urlFormattedLocation}/${urlFormattedRole}`
      } else if (roleName) {
        title = `${companyName} Reviews: What Employees Say About ${roleName} Jobs`
        description = `Read real reviews from ${companyName} employees working in ${roleName} roles. Learn about culture, pay, and management based on actual job experiences.`
        canonicalUrl = `${baseUrl}/reviews/usa/${urlFormattedRole}`
      } else if (formattedLocation) {
        title = `${companyName} Reviews in ${formattedLocation}: Employee Ratings & Feedback`
        description = `Discover what employees say about working at ${companyName} in ${formattedLocation}. See real reviews, pros, cons, and more from current and former staff.`
        canonicalUrl = `${baseUrl}/reviews/${urlFormattedLocation}`
      } else {
        title = `${companyName} Employees Reviews: What It's Like to Work There`
        description = `Explore what it's like to work at ${companyName} from real employee reviews. Discover pros, cons, ratings, and more before you apply.`
        canonicalUrl = `${baseUrl}/reviews`
      }
      break
    case 'salaries':
      if (roleName && formattedLocation) {
        title = `How Much Does ${companyName} Pay ${roleName} in ${formattedLocation}? | ${year} Data`
        description = `How much do ${companyName} ${roleName} employees make in ${formattedLocation}? Find out the average salary for ${companyName} ${roleName} workers in ${formattedLocation} on employers.io.`
        canonicalUrl = `${baseUrl}/salaries/${urlFormattedLocation}/${urlFormattedRole}`
      } else if (roleName) {
        title = `How Much Does ${companyName} Pay ${roleName} Jobs? | ${year} Salaries`
        description = `How much do ${companyName} ${roleName} employees make? Find out the average salary for ${companyName} ${roleName} workers on employers.io.`
        canonicalUrl = `${baseUrl}/salaries/usa/${urlFormattedRole}`
      } else if (formattedLocation) {
        title = `How Much Does ${companyName} Pay in ${formattedLocation}? | ${year} Salaries`
        description = `How much do ${companyName} employees make in ${formattedLocation}? Find out the average salary for ${companyName} workers in ${formattedLocation} on employers.io.`
        canonicalUrl = `${baseUrl}/salaries/${urlFormattedLocation}`
      } else {
        title = `How Much Does ${companyName} Pay? | ${companyName} Salaries ${year}`
        description = `How much does ${companyName} Pay? Find out what the average ${companyName} salary. Discover salary insights on employers.io.`
        canonicalUrl = `${baseUrl}/salaries`
      }
      break
    case 'jobs':
      if (formattedLocation) {
        title = `${companyName} Jobs in ${formattedLocation}: Job Openings and Opportunities (${monthsYear})`
        description = `Find ${companyName} jobs in ${formattedLocation}. Explore roles, salaries, and exciting opportunities near you on employers.io.`
        canonicalUrl = `${baseUrl}/jobs/${urlFormattedLocation}`
      } else {
        title = `${companyName} Jobs: Job Openings and Opportunities (${monthsYear})`
        description = `Find ${companyName} jobs near you. Explore roles, salaries, and exciting opportunities on employers.io.`
        canonicalUrl = `${baseUrl}/jobs`
      }
      if (page && page > 1) {
        canonicalUrl = `${canonicalUrl}?page=${page}`
      }
      break
    default:
      title = `${companyName} | Company Info, Reviews, Salaries | Employers.io`
      description = `Learn about ${companyName}. Read employee reviews, discover salary information, find job openings, and get insights into company culture.`
      canonicalUrl = baseUrl
  }
  const hasRole = Boolean(roleParam)
  const hasLocation = Boolean(location && (location.city || location.state))
  const shouldNoIndex
    = invalidLocation
    || invalidRole
    || noIndex
    || (section !== 'salaries' && section !== 'reviews' && section !== 'jobs' && (hasRole || hasLocation))
  const breadcrumbSchema = generateBreadcrumbSchema({
    companyName,
    companySlug,
    section,
    location,
    roleParam
  })
  const navLinks = companySlug ? companyLinks(companySlug) : []
  const combinedLinks = [...menuLinks.b2c, ...navLinks]
  const navigationSchema = generateItemListShema(combinedLinks)
  const relativeCanonicalUrl = canonicalUrl.replace('https://employers.io', '')
  const schemas = []
  if (breadcrumbSchema) {
    schemas.push(JSON.stringify(breadcrumbSchema))
  }
  if (navigationSchema) {
    schemas.push(JSON.stringify(navigationSchema))
  }
  if (section === 'dress-code' && dressCodeData?.faq) {
    const faqItems = []
    Object.entries(dressCodeData.faq).forEach(([question, answerObj]) => {
      if (typeof answerObj === 'object') {
        let answerText = ''
        if ('Answer' in answerObj) {
          answerText = String(answerObj.Answer)
        } else {
          answerText = Object.entries(answerObj)
            .map(([section, text]) => `${section}: ${text}`)
            .join('<br>')
        }
        if (question && answerText) {
          faqItems.push({
            question,
            answer: answerText
          })
        }
      }
    })
    if (faqItems.length > 0) {
      const faqSchema = generateFAQPageSchema(faqItems, canonicalUrl)
      if (faqSchema) {
        schemas.push(JSON.stringify(faqSchema))
      }
    }
  }
  if (section === 'drug-test-policy' && drugTestData?.faq) {
    const faqItems = []
    Object.entries(drugTestData.faq).forEach(([question, answerObj]) => {
      if (typeof answerObj === 'object') {
        let answerText = ''
        if ('Answer' in answerObj) {
          answerText = String(answerObj.Answer)
        } else {
          answerText = Object.entries(answerObj)
            .map(([section, text]) => `${section}: ${text}`)
            .join('<br>')
        }
        if (question && answerText) {
          faqItems.push({
            question,
            answer: answerText
          })
        }
      }
    })
    if (faqItems.length > 0) {
      const faqSchema = generateFAQPageSchema(faqItems, canonicalUrl)
      if (faqSchema) {
        schemas.push(JSON.stringify(faqSchema))
      }
    }
  }
  if (section === 'hiring-process' && hiringProcessData) {
    if (hiringProcessData.faq) {
      const faqItems = []
      Object.entries(hiringProcessData.faq).forEach(([question, answerObj]) => {
        if (typeof answerObj === 'object') {
          let answerText = ''
          if ('Answer' in answerObj) {
            answerText = String(answerObj.Answer)
          } else {
            answerText = Object.entries(answerObj)
              .map(([section, text]) => `${section}: ${text}`)
              .join('<br>')
          }
          if (question && answerText) {
            faqItems.push({
              question,
              answer: answerText
            })
          }
        }
      })
      if (faqItems.length > 0) {
        const faqSchema = generateFAQPageSchema(faqItems, canonicalUrl)
        if (faqSchema) {
          schemas.push(JSON.stringify(faqSchema))
        }
      }
    }
    if (hiringProcessData.hiring_steps && Object.keys(hiringProcessData.hiring_steps).length > 0) {
      const howToSteps = Object.entries(hiringProcessData.hiring_steps).map(([stepTitle, stepDescription], index) => ({
        '@type': 'HowToStep',
        position: index + 1,
        name: String(stepTitle),
        text: String(stepDescription)
      }))
      const howToSchema = {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: `${companyName} Hiring Process`,
        description: hiringProcessData.overview || `Learn about the step-by-step hiring process at ${companyName}.`,
        step: howToSteps,
        totalTime: hiringProcessData.quick_facts?.Timeline || undefined
      }
      schemas.push(JSON.stringify(howToSchema))
    }
  }
  if (section === 'salaries' && salaryDetail?.success && salaryDetail?.data) {
    const salaryData = salaryDetail.data
    const displayRole
      = salaryData.job_title !== 'All Positions' && roleName
        ? roleName
        : salaryData.job_title
    const displayLocation = formattedLocation || salaryData.location
    const currentDate = new Date().toISOString().split('T')[0]
    const stateFullName = location.state
      ? getStateFullName(location.state)
      : undefined
    const methodologyText = generateSalaryMethodologyText(companyName || '', salaryData.count)
    const schemaDescription = methodologyText
    const salarySchema = generateEstimatedSalarySchema({
      occupationName: title,
      description: schemaDescription,
      minSalary: salaryData.base_salary.P10,
      maxSalary: salaryData.base_salary.P90,
      medianSalary: salaryData.base_salary.P50,
      percentile10: salaryData.base_salary.P10,
      percentile25: salaryData.base_salary.P25,
      percentile75: salaryData.base_salary.P75,
      percentile90: salaryData.base_salary.P90,
      city: location.city || undefined,
      state: stateFullName,
      country: 'USA',
      url: canonicalUrl,
      occupationalCategory:
        displayRole !== 'All Positions' ? roleInfo?.soc_code : undefined,
      industry: roleInfo.title,
      sampleSize: salaryData.count,
      lastReviewed: currentDate,
      companyName: companyName,
      companyPermalink: companySlug,
      roleDescription: roleInfo.description
    })
    if (salarySchema) {
      schemas.push(JSON.stringify(salarySchema))
    }
    if (salaryPageTitle) {
      const salary = salaryData.base_salary.P50
      const hourlyWage = salary / 2080
      const formattedSalaryData: SalaryFAQData = {
        avg_annual: salary,
        avg_hourly: hourlyWage,
        keyword: displayRole,
        location: displayLocation,
        sample_size: salaryData.count,
        pay_frequency: 'biweekly'
      }
      let faqType = 'company'
      if (roleName && formattedLocation) {
        faqType = 'role-location'
      } else if (roleName) {
        faqType = 'role'
      } else if (formattedLocation) {
        faqType = 'location'
      }
      const faqEntries = generateSalaryFAQQuestions(
        companyName,
        formattedSalaryData,
        faqType as any
      )
      const faqItems = convertFAQEntriesToSchemaItems(faqEntries)
      const salaryDescription = generateSalaryDescription(
        salaryData,
        companyName || '',
        { city: location.city, state: location.state },
        roleParam ? decodeURIComponent(roleParam).replace(/-/g, ' ') : undefined
      )
      if (salaryPageTitle) {
        faqItems.unshift({
          question: salaryPageTitle,
          answer: salaryDescription
        })
      }
      const faqSchema = generateFAQPageSchema(faqItems, canonicalUrl)
      if (faqSchema) {
        schemas.push(JSON.stringify(faqSchema))
      }
    }
  }
  if (section === 'reviews' && reviewStats && companyName && reviewStats.total > 0) {
    const employerRatingSchema = generateEmployerAggregateRatingSchema({
      companyName,
      ratingValue: reviewStats.overallRating,
      ratingCount: reviewStats.total
    })
    if (employerRatingSchema) {
      schemas.push(JSON.stringify(employerRatingSchema))
    }
  }
  if (section === 'reviews' && reviews && reviews.length > 0 && companyName && companySlug) {
    const reviewSchemas = generateReviewsListSchema(reviews, {
      name: companyName,
      permalink: companySlug,
      logo: companyLogo,
      description: companyDescription,
      website: companyWebsite
    })
    reviewSchemas.forEach((reviewSchema) => {
      if (reviewSchema) {
        schemas.push(JSON.stringify(reviewSchema))
      }
    })
  }
  applyMetaData({
    title,
    description,
    canonical: relativeCanonicalUrl,
    index: !shouldNoIndex,
    follow: !shouldNoIndex,
    schemas
  })
  return {
    title,
    description,
    canonicalUrl,
    breadcrumbSchema,
    navigationSchema
  }
}